import {
  type ChatDelegatePreview,
  type ChatState,
  type ChatTaskPreview,
  type ChatToolEvent,
  COPILOT_CHAT_MAX_CHARS,
  COPILOT_CHAT_MAX_MESSAGES,
  COPILOT_MESSAGES,
  type CopilotContextRef,
  delegateNeedsFullReview,
} from '@fos/shared/contracts/copilot';
import { COPILOT_ACTION_MESSAGES, type CopilotExecutedStep } from '@fos/shared/contracts/copilot-actions';
import type { TimeEntryParsePreview } from '@fos/shared/contracts/time';
import {
  CHAT_STATE_LABELS,
  CHAT_STATE_TONES,
  COPILOT_TOOL_LABELS,
  DELEGATE_RUNTIME_LABELS,
  delegationReasonText,
} from '@fos/shared/labels';
import { ArrowUp, Check, History, RotateCcw, Settings, Sparkles } from 'lucide-react';
import {
  createContext,
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Textarea } from '../../../ui/Input';
import { Spinner } from '../../../ui/Spinner';
import { EmptyState } from '../../../ui/States';
import { Status, type Tone } from '../../../ui/Status';
import { DelegateToClaudeDialog } from '../../agents/components/DelegateToClaudeDialog';
import { ChatNoteConfirm } from '../../history/ChatNoteConfirm';
import { Composer } from '../../mail';
import { DictationStatusLine, MicButton } from '../../voice/components/MicButton';
import { useDictation } from '../../voice/dictation';
import { SPEECH_MESSAGES } from '../../voice/speech';
import { useSpeechStatus } from '../../voice/speech-status';
import { type ChatConfirmed, type ChatTurn, copilotChat, newTurnId, useCopilotChat } from '../chat-store';
import { conversationIdFor } from '../conversation-id';
import { copilotErrorInfo, providerText, toolEventHref } from '../copilot-ui';
import { useCopilotInvalidate, useCopilotStatus } from '../hooks';
import {
  AssignedElsewhere,
  ChatPlanCard,
  PlanDone,
  UndoButton,
  undoStepsOf,
  useCopilotActionsInvalidate,
} from './ChatActions';
import { ConversationHistory } from './ConversationHistory';
import { CopilotDock } from './CopilotDock';
import { CopilotUsage } from './CopilotUsage';
import { ManualShortcuts } from './ManualShortcuts';
import { TimePreviewCard } from './TimePreviewCard';

/**
 * Panel „Zapytaj AI” (⌘K → „Zapytaj AI…”, przycisk w Dziś; MVP §12): rozmowa z Copilotem, który działa z uprawnieniami
 * osoby i narzędziami = te same usługi co UI (wyszukiwanie, kontekst klienta, zadania, czas, szkic maila, wysyłka przez
 * Akceptację, propozycja pamięci). Nic nieodwracalnego nie dzieje się bez zgody: wpis czasu = podgląd do potwierdzenia,
 * mail = approval_id („czeka na akceptację”, nie „wysłano”), pamięć = propozycja. Brak narzędzia do sejfu: prośba
 * o hasło kończy się odmową. Stan AI zawsze widoczny (AI pracuje / Gotowe / Czeka na akceptację / Wybierz).
 * Historia rozmowy jest tylko w tej karcie przeglądarki (serwer zapisuje `ai_jobs` z krótkim opisem).
 *
 * Fala 10 (bąbel AI): panel dokowany z prawej na desktopie (treść obok dostępna), pełny ekran na telefonie
 * (`CopilotDock`); rozmowa w sesji karty (`chat-store`: zostaje po zamknięciu, zmianie ekranu i odświeżeniu); kontekst
 * bieżącego ekranu przy każdym pytaniu (zmiana ekranu = znacznik „Kontekst: …” w rozmowie, bez kasowania historii);
 * mikrofon (dyktowanie do pola — do poprawy przed wysłaniem, opcja „Wyślij po zakończeniu mówienia”); licznik
 * zużycia AI z `ai_jobs`.
 *
 * 10.10.2026 (DECISIONS [S] „Mikrofon w dolnym menu = Copilot”): `voice` = panel otwarty mikrofonem z dolnego menu
 * na telefonie — dyktowanie startuje od razu (ten sam mechanizm co mikrofon przy polu), „Wyślij po zakończeniu
 * mówienia” jest włączone dla tego otwarcia (bez zapisu w przeglądarce), fokus nie trafia do pola (bez klawiatury na
 * ekranie), pod polem skróty „Wpisz ręcznie: czas · zadanie · notatka”. AI niedostępne = komunikat i skróty, bez
 * dyktowania.
 */

type Turn = ChatTurn;

/** Tura odpowiedzi, w której jest podgląd do potwierdzenia — potwierdzenie gasi plakietkę bąbla. */
const TurnContext = createContext<string | null>(null);

/** Podgląd z odpowiedzi Copilota potwierdzony (zadanie, wpis czasu, zlecenie, plan): bąbel przestaje czekać. */
export function useResolveCopilotTurn(): () => void {
  const turnId = useContext(TurnContext);
  return useCallback(() => {
    if (turnId) copilotChat.resolve(turnId);
  }, [turnId]);
}

const SEND_AFTER_SPEECH_KEY = 'fos.copilot.sendAfterSpeech';

function readSendAfterSpeech(): boolean {
  try {
    return window.localStorage.getItem(SEND_AFTER_SPEECH_KEY) === '1';
  } catch {
    return false;
  }
}

const EVENT_TONES: Record<ChatToolEvent['status'], Tone> = {
  ok: 'success',
  error: 'danger',
  needs_confirmation: 'warning',
  waiting_approval: 'accent',
  needs_choice: 'warning',
};

const turnId = newTurnId;

/** Wiadomości do wysłania: bez tur z błędem i znaczników, ostatnie 20, każda w limicie znaków. */
export function chatMessagesOf(turns: readonly Turn[]): { role: 'user' | 'assistant'; content: string }[] {
  return turns
    .filter(
      (t): t is Turn & { role: 'user' | 'assistant' } => t.role !== 'note' && !t.error && Boolean(t.content.trim()),
    )
    .map((t) => ({ role: t.role, content: t.content.trim().slice(0, COPILOT_CHAT_MAX_CHARS) }))
    .slice(-COPILOT_CHAT_MAX_MESSAGES);
}

export function AskAiPanel({
  open,
  onOpenChange,
  contextRef = null,
  contextLabel = null,
  draft = null,
  openedAt = 0,
  voice = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contextRef?: CopilotContextRef | null;
  contextLabel?: string | null;
  /** tekst wstawiany do pola przy otwarciu (bez wysyłania) */
  draft?: string | null;
  /** znacznik otwarcia (`askAi.open`) — ustawia tekst z `draft` */
  openedAt?: number;
  /** otwarte mikrofonem z dolnego menu: dyktowanie od razu, wysyłka po zakończeniu mówienia, skróty ręczne */
  voice?: boolean;
}) {
  const access = useAccess();
  const status = useCopilotStatus(open);
  const chat = trpc.copilot.chat.useMutation();
  const invalidate = useCopilotInvalidate();
  const invalidateActions = useCopilotActionsInvalidate();
  const { turns } = useCopilotChat();
  const [input, setInput] = useState('');
  const [draftId, setDraftId] = useState<string | null>(null);
  // Fala 10 (historia promptów): lista moich zapisanych rozmów zamiast bieżącej rozmowy
  const [showHistory, setShowHistory] = useState(false);
  const [sendAfterSpeech, setSendAfterSpeech] = useState(readSendAfterSpeech);
  // otwarcie mikrofonem z dolnego menu, na które czeka start dyktowania (znacznik otwarcia)
  const [listenFor, setListenFor] = useState<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const contextKey = contextRef ? `${contextRef.type}:${contextRef.id}` : '';
  const setTurns = copilotChat.update;

  // Fala 10: inny ekran = nowy kontekst kolejnych pytań; rozmowa zostaje, w historii znacznik „Kontekst: …”
  const lastContext = useRef(contextKey);
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy zmianie kontekstu
  useEffect(() => {
    if (lastContext.current === contextKey) return;
    lastContext.current = contextKey;
    setTurns((prev) => {
      if (prev.length === 0) return prev;
      const note: Turn = {
        id: turnId(),
        role: 'note',
        content: contextLabel ? `Kontekst: ${contextLabel}` : 'Bez kontekstu ekranu',
      };
      return prev[prev.length - 1]?.role === 'note' ? [...prev.slice(0, -1), note] : [...prev, note];
    });
  }, [contextKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: tekst z otwarcia (np. „Zapamiętaj, że …”)
  useEffect(() => {
    if (!open) return;
    // wejście głosem: wysyłka po zakończeniu mówienia tylko dla tego otwarcia; zwykłe otwarcie = opcja zapamiętana
    setSendAfterSpeech(voice ? true : readSendAfterSpeech());
    setListenFor(voice ? openedAt : null);
    if (voice) {
      // nowe polecenie głosem: pole od zera, bez fokusu w polu (klawiatura telefonu zasłoniłaby panel)
      setInput('');
      return;
    }
    if (draft) setInput(draft);
    // fokus w polu wiadomości z kursorem na końcu wstawionego tekstu: Enter wysyła pytanie (nie zamyka panelu)
    const timer = window.setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [openedAt]);

  const count = turns.length;
  // biome-ignore lint/correctness/useExhaustiveDependencies: przewinięcie przy nowej wiadomości
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [count, chat.isPending]);

  const unavailable = status.data ? !status.data.available : false;
  const last = [...turns].reverse().find((t) => t.role === 'assistant');
  // 10.10.2026: potwierdzony podgląd / wykonany plan = „Gotowe” (nie „Czeka na Twoje potwierdzenie”)
  const state: ChatState | 'working' | 'error' | null = chat.isPending
    ? 'working'
    : last?.error
      ? 'error'
      : last?.resolved && last.result
        ? 'done'
        : (last?.result?.state ?? null);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || chat.isPending || unavailable) return;
    setShowHistory(false);
    const userTurn: Turn = { id: turnId(), role: 'user', content: content.slice(0, COPILOT_CHAT_MAX_CHARS) };
    const history = [...copilotChat.getSnapshot().turns, userTurn];
    setTurns(() => history);
    setInput('');
    try {
      const messages = chatMessagesOf(history);
      // Fala 10: identyfikator rozmowy — powtórzone polecenie nie tworzy duplikatu
      const result = await chat.mutateAsync({ messages, contextRef, conversationId: conversationIdFor(messages) });
      setTurns((prev) => [...prev, { id: turnId(), role: 'assistant', content: result.reply, result }]);
      const tools = new Set(result.toolEvents.filter((e) => e.status === 'ok').map((e) => e.tool));
      if (tools.has('create_task')) void invalidate.tasks();
      if (result.toolEvents.some((e) => e.status === 'ok' && e.undo?.length)) void invalidateActions();
      void invalidate.jobs();
    } catch (err) {
      setTurns((prev) => [...prev, { id: turnId(), role: 'assistant', content: '', error: copilotErrorInfo(err) }]);
    }
  };
  const sendRef = useRef(send);
  sendRef.current = send;

  // ── Mowa na tekst (Fala 10): transkrypcja na żywo do pola, do poprawy przed wysłaniem ─────────────────────────
  const speechStatus = useSpeechStatus(open);
  const followSpeech = useRef(false);
  const dictation = useDictation({
    lang: 'pl-PL',
    engine: speechStatus.engine,
    serverAvailable: speechStatus.serverAvailable,
    serverMessage: speechStatus.serverMessage,
    autoFinish: sendAfterSpeech,
    onFinished(text) {
      followSpeech.current = false;
      setInput(text);
      if (sendAfterSpeech) void sendRef.current(text);
    },
  });
  useEffect(() => {
    if (!followSpeech.current) return;
    setInput([dictation.finalText, dictation.interimText].filter(Boolean).join(' '));
  }, [dictation.finalText, dictation.interimText]);
  // zamknięcie panelu = koniec słuchania (mikrofon nie zostaje włączony w tle)
  // biome-ignore lint/correctness/useExhaustiveDependencies: reakcja na zamknięcie
  useEffect(() => {
    if (!open && dictation.active) {
      followSpeech.current = false;
      dictation.cancel();
    }
  }, [open]);

  // wejście głosem: dyktowanie startuje, gdy wiadomo, czy AI działa i którym silnikiem mowy (status z serwera);
  // AI niedostępne albo brak mowy w przeglądarce = bez dyktowania (komunikat, pole i skróty ręczne)
  const copilotReady = Boolean(status.data) || status.isError;
  // biome-ignore lint/correctness/useExhaustiveDependencies: start raz na otwarcie, gdy statusy są znane
  useEffect(() => {
    if (listenFor === null || listenFor !== openedAt || !open) return;
    if (!copilotReady || !speechStatus.ready) return;
    setListenFor(null);
    if (status.data?.available !== true || !dictation.supported || dictation.active) return;
    followSpeech.current = true;
    dictation.start('');
  }, [listenFor, openedAt, open, copilotReady, speechStatus.ready]);

  const toggleSendAfterSpeech = (value: boolean) => {
    setSendAfterSpeech(value);
    try {
      window.localStorage.setItem(SEND_AFTER_SPEECH_KEY, value ? '1' : '0');
    } catch {
      // bez pamięci przeglądarki: opcja do końca sesji
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (dictation.active) {
      followSpeech.current = false;
      dictation.cancel();
    }
    void send(input);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (dictation.active) {
        followSpeech.current = false;
        dictation.cancel();
      }
      void send(input);
    }
  };

  const suggest = (text: string) => {
    setInput(text);
    inputRef.current?.focus();
  };

  const suggestions = [
    'Jakie mam zadania na dziś?',
    contextLabel ? `Co wiemy o ${contextLabel}?` : 'Co wiemy o REA?',
    contextLabel ? `Dodaj zadanie: … dla ${contextLabel}` : 'Dodaj zadanie: …',
    'Wpisz czas: REA 1h40 poprawki od 10:20',
    'Zapamiętaj, że …',
  ];

  return (
    <>
      <CopilotDock
        open={open}
        onOpenChange={onOpenChange}
        // wejście głosem: fokus na „Zamknij” (bez klawiatury ekranowej w trakcie dyktowania)
        initialFocusRef={voice ? undefined : inputRef}
        id="copilot-panel"
        title="Zapytaj AI"
        description={
          contextLabel
            ? `Kontekst: ${contextLabel}`
            : 'Pytaj o klientów, zadania i czas. Działam z Twoimi uprawnieniami.'
        }
        footer={
          <div className="flex flex-col gap-2">
            <DictationStatusLine dictation={dictation} />
            <form onSubmit={submit} className="flex w-full items-end gap-2" aria-label="Wiadomość do AI">
              <Textarea
                ref={inputRef}
                rows={2}
                value={input}
                onChange={(e) => {
                  // ręczna poprawka w trakcie dyktowania: koniec słuchania, tekst zostaje
                  if (dictation.active && followSpeech.current) {
                    followSpeech.current = false;
                    dictation.cancel();
                  }
                  setInput(e.target.value);
                }}
                onKeyDown={onKeyDown}
                maxLength={COPILOT_CHAT_MAX_CHARS}
                disabled={unavailable}
                aria-label="Wiadomość do AI"
                placeholder={
                  unavailable
                    ? 'AI jest niedostępne'
                    : dictation.status === 'listening' || dictation.status === 'recording'
                      ? 'Mów…'
                      : 'Napisz polecenie albo pytanie…'
                }
                className="min-h-[60px] flex-1 resize-none"
              />
              <div className="flex flex-col items-center gap-1.5">
                {!unavailable && dictation.supported && (
                  <MicButton
                    dictation={dictation}
                    base={input}
                    onStart={() => {
                      followSpeech.current = true;
                    }}
                  />
                )}
                <Button
                  type="submit"
                  variant="primary"
                  icon={ArrowUp}
                  aria-label="Wyślij"
                  disabled={!input.trim() || unavailable}
                  loading={chat.isPending}
                />
              </div>
            </form>
            {!unavailable && dictation.supported && (
              <Checkbox
                checked={sendAfterSpeech}
                onCheckedChange={toggleSendAfterSpeech}
                label="Wyślij po zakończeniu mówienia"
                className="text-caption"
              />
            )}
            {voice && !unavailable && speechStatus.ready && !dictation.supported && (
              <p className="text-caption text-fg-muted">{SPEECH_MESSAGES.unsupported}</p>
            )}
            {(voice || unavailable) && <ManualShortcuts onOpen={() => onOpenChange(false)} />}
          </div>
        }
      >
        <div className="flex min-h-full flex-col gap-4">
          <div className="sticky top-0 z-10 -mx-5 flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1 bg-canvas px-5 pb-2">
            {state && state !== 'error' && <Status tone={CHAT_STATE_TONES[state]}>{CHAT_STATE_LABELS[state]}</Status>}
            {state === 'error' && <Status tone="danger">Błąd</Status>}
            {status.data?.simulated && <Status tone="accent">Symulacja</Status>}
            <span className="flex-1" />
            <Button
              variant={showHistory ? 'secondary' : 'ghost'}
              size="s"
              icon={History}
              aria-pressed={showHistory}
              onClick={() => setShowHistory((v) => !v)}
              disabled={chat.isPending}
            >
              Historia
            </Button>
            {turns.length > 0 && !showHistory && (
              <Button
                variant="ghost"
                size="s"
                icon={RotateCcw}
                onClick={() => {
                  copilotChat.clear();
                  chat.reset();
                  inputRef.current?.focus();
                }}
                disabled={chat.isPending}
              >
                Nowa rozmowa
              </Button>
            )}
            <div className="w-full">
              <CopilotUsage enabled={open && Boolean(status.data?.enabled)} />
            </div>
          </div>

          {status.data?.simulated && turns.length === 0 && (
            <p className="text-caption text-fg-muted">{COPILOT_MESSAGES.simulated}</p>
          )}

          {showHistory ? (
            <ConversationHistory
              onClose={() => setShowHistory(false)}
              onResumed={() => {
                chat.reset();
                inputRef.current?.focus();
              }}
            />
          ) : unavailable && status.data ? (
            <EmptyState
              icon={Sparkles}
              title={
                status.data.memberBlock === 'partner'
                  ? "Copilot działa w Twoim workspace'ie"
                  : status.data.enabled && !status.data.memberBlock
                    ? 'AI jest niedostępne'
                    : 'AI jest wyłączone'
              }
              description={status.data.message ?? COPILOT_MESSAGES.unavailable}
              action={
                access.isManagerOrAbove && !status.data.memberBlock ? (
                  <Button asChild variant="secondary" icon={Settings}>
                    <Link to="/ustawienia/ai?zakladka=model" onClick={() => onOpenChange(false)}>
                      Ustawienia AI
                    </Link>
                  </Button>
                ) : undefined
              }
            />
          ) : turns.length === 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-body-s text-fg-secondary">Na przykład:</p>
              <ul className="flex flex-col">
                {suggestions.map((s) => (
                  <li key={s}>
                    <button
                      type="button"
                      onClick={() => suggest(s)}
                      className="flex min-h-10 w-full items-center border-b border-line text-left text-body-s text-fg hover:text-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
                    >
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div role="log" aria-live="polite" aria-label="Rozmowa z AI" className="flex flex-col gap-4">
              {turns.map((turn) =>
                turn.role === 'note' ? (
                  <p key={turn.id} className="flex items-center gap-2 text-caption text-fg-muted">
                    <span aria-hidden className="h-px flex-1 bg-line" />
                    {turn.content}
                    <span aria-hidden className="h-px flex-1 bg-line" />
                  </p>
                ) : turn.role === 'user' ? (
                  <div
                    key={turn.id}
                    className="ml-8 self-end rounded-xl bg-elevated px-3 py-2 text-body-s break-words whitespace-pre-wrap text-fg"
                  >
                    {turn.content}
                  </div>
                ) : (
                  <TurnContext.Provider key={turn.id} value={turn.id}>
                    <AssistantTurn
                      turn={turn}
                      onChoice={(text) => void send(text)}
                      onOpenDraft={setDraftId}
                      onRetry={() => {
                        const lastUser = [...turns].reverse().find((t) => t.role === 'user');
                        setTurns((prev) => prev.filter((t) => t.id !== turn.id && t.id !== lastUser?.id));
                        if (lastUser) void send(lastUser.content);
                      }}
                      busy={chat.isPending}
                    />
                  </TurnContext.Provider>
                ),
              )}
              {chat.isPending && (
                <p role="status" className="flex items-center gap-2 text-body-s text-fg-muted">
                  <Spinner size={14} /> AI pracuje…
                </p>
              )}
            </div>
          )}
          <div ref={endRef} />
        </div>
      </CopilotDock>
      <Composer
        open={draftId !== null}
        onOpenChange={(o) => {
          if (!o) setDraftId(null);
        }}
        draftId={draftId}
        title="Szkic od AI"
      />
    </>
  );
}

function AssistantTurn({
  turn,
  onChoice,
  onOpenDraft,
  onRetry,
  busy,
}: {
  turn: Turn;
  onChoice: (text: string) => void;
  onOpenDraft: (draftId: string) => void;
  onRetry: () => void;
  busy: boolean;
}) {
  if (turn.error) {
    return (
      <div className="flex flex-col gap-2">
        <FormMessage>{turn.error.message}</FormMessage>
        {!turn.error.manualWorks || turn.error.state === 'unavailable' ? (
          <div>
            <Button variant="ghost" size="s" icon={RotateCcw} onClick={onRetry} disabled={busy}>
              Spróbuj ponownie
            </Button>
          </div>
        ) : null}
      </div>
    );
  }
  const result = turn.result;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-on-accent"
        >
          <Sparkles size={13} strokeWidth={2} />
        </span>
        <p className="min-w-0 flex-1 text-body-s break-words whitespace-pre-wrap text-fg">{turn.content}</p>
      </div>
      {result && result.toolEvents.length > 0 && (
        <ul aria-label="Działania AI" className="ml-8 flex flex-col gap-2">
          {result.toolEvents.map((e, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: zdarzenia jednej odpowiedzi w stałej kolejności
            <li key={i}>
              <ToolEventRow event={e} turn={turn} index={i} onChoice={onChoice} onOpenDraft={onOpenDraft} busy={busy} />
            </li>
          ))}
        </ul>
      )}
      {/* Fala 10: kilka wykonanych działań w jednej odpowiedzi = „Cofnij wszystko” (od ostatniego) */}
      {result && result.toolEvents.filter((e) => e.status === 'ok' && e.undo?.length).length > 1 && (
        <div className="ml-8">
          <UndoButton
            steps={undoStepsOf(result.toolEvents.filter((e) => e.status === 'ok'))}
            label="Cofnij wszystko"
            undone={turn.undone?.all ?? null}
            onUndone={(summary) => copilotChat.recordUndone(turn.id, 'all', summary)}
          />
        </div>
      )}
      {result && (
        <p className="ml-8 text-caption text-fg-muted">
          {providerText(result.meta)}
          {result.meta.simulated ? ' · nic nie zostało wysłane do dostawcy' : ''}
        </p>
      )}
    </div>
  );
}

/** Plan z tury wykonany (wynik z rozmowy; z pamięci sesji sprawdzony kształt). */
function executedOf(turn: Turn, index: number): CopilotExecutedStep[] | null {
  const steps = turn.executed?.[String(index)];
  return Array.isArray(steps) ? steps : null;
}

/** Potwierdzony podgląd z tury: „Dodano zadanie #12 …” z linkiem (bez ponownego „Dodaj”). */
function ConfirmedRow({ confirmed }: { confirmed: ChatConfirmed }) {
  const href = confirmed.href?.startsWith('/') ? confirmed.href : null;
  return (
    <div className="flex flex-col gap-1 rounded-md border border-line bg-canvas p-3">
      <p className="flex flex-wrap items-baseline gap-x-2 text-caption text-fg">
        <Check size={14} strokeWidth={1.75} aria-hidden className="shrink-0 self-center text-success" />
        <span className="min-w-0 flex-1 break-words">{confirmed.label}</span>
        {href && (
          <Link to={href} className="text-accent hover:underline">
            Otwórz
          </Link>
        )}
      </p>
      <AssignedElsewhere assignee={confirmed.assignee} />
    </div>
  );
}

function ToolEventRow({
  event,
  turn,
  index,
  onChoice,
  onOpenDraft,
  busy,
}: {
  event: ChatToolEvent;
  turn: Turn;
  index: number;
  onChoice: (text: string) => void;
  onOpenDraft: (draftId: string) => void;
  busy: boolean;
}) {
  const href = toolEventHref(event);
  const draftId = event.link?.type === 'email_draft' ? event.link.id : null;
  const resolveTurn = useResolveCopilotTurn();
  // 10.10.2026: wynik „Wykonaj” / potwierdzenia jest w turze rozmowy — zostaje po zamknięciu panelu, zmianie ekranu
  // i odświeżeniu (wcześniej żył tylko w stanie karty: po ponownym otwarciu wracało „Do potwierdzenia”)
  const executed = executedOf(turn, index);
  const confirmed = turn.confirmed?.[String(index)] ?? null;
  const settled = Boolean(executed || confirmed);
  const confirm = (c: ChatConfirmed) => copilotChat.recordConfirmed(turn.id, index, c);
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-surface p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Status tone={settled ? 'success' : EVENT_TONES[event.status]}>{COPILOT_TOOL_LABELS[event.tool]}</Status>
        <span className="flex-1" />
        {href && (
          <Link to={href} className="text-caption text-accent hover:underline">
            {event.approvalId ? 'Otwórz w Akceptacjach' : 'Otwórz'}
          </Link>
        )}
        {draftId && (
          <button
            type="button"
            onClick={() => onOpenDraft(draftId)}
            className="rounded-xs text-caption text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
          >
            Otwórz szkic
          </button>
        )}
      </div>
      <p className="text-body-s break-words text-fg">{executed ? COPILOT_ACTION_MESSAGES.planExecuted : event.label}</p>
      {/* 10.10.2026: dodane zadanie przypisane innej osobie — gdzie je znaleźć */}
      {event.status === 'ok' && event.tool === 'create_task' && <AssignedElsewhere assignee={event.assignee} />}
      {/* Fala 10: wykonane działanie z „Cofnij”; plan do potwierdzenia (wybór, dane firmy po NIP, pytanie) */}
      {event.status === 'ok' && event.undo && event.undo.length > 0 && (
        <div>
          <UndoButton
            steps={event.undo}
            undone={turn.undone?.[`event:${index}`] ?? null}
            onUndone={(summary) => copilotChat.recordUndone(turn.id, `event:${index}`, summary)}
          />
        </div>
      )}
      {event.plan ? (
        <ChatPlanCard
          plan={event.plan}
          onDone={resolveTurn}
          executed={executed}
          onExecuted={(steps) => copilotChat.recordExecuted(turn.id, index, steps)}
          undone={turn.undone?.[`plan:${index}`] ?? null}
          onUndone={(summary) => copilotChat.recordUndone(turn.id, `plan:${index}`, summary)}
        />
      ) : executed ? (
        // odtworzona rozmowa (po odświeżeniu): bez planu, ale z wynikiem wykonania
        <div className="rounded-md border border-line bg-canvas p-3">
          <PlanDone
            steps={executed}
            undone={turn.undone?.[`plan:${index}`] ?? null}
            onUndone={(summary) => copilotChat.recordUndone(turn.id, `plan:${index}`, summary)}
          />
        </div>
      ) : null}
      {event.status === 'waiting_approval' && (
        <p className="text-caption text-fg-muted">Nic nie zostało wykonane — czeka na zgodę w Akceptacjach.</p>
      )}
      {confirmed ? (
        <ConfirmedRow confirmed={confirmed} />
      ) : (
        <>
          {event.timePreview && (
            <ChatTimePreview preview={event.timePreview} onSaved={(label) => confirm({ label, href: '/czas' })} />
          )}
          {event.taskPreview && <ChatTaskConfirm preview={event.taskPreview} onConfirmed={confirm} />}
          {event.delegatePreview && <ChatDelegateConfirm preview={event.delegatePreview} onConfirmed={confirm} />}
          {event.notePreview && (
            <ChatNoteConfirm
              preview={event.notePreview}
              onDone={() => {
                resolveTurn();
                const note = event.notePreview;
                if (note)
                  confirm({ label: `Zapisano notatkę klienta ${note.clientName}`, href: `/klienci/${note.clientId}` });
              }}
            />
          )}
        </>
      )}
      {event.status === 'needs_choice' && event.tool !== 'log_time' && event.choices && event.choices.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {event.choices.map((c) => (
            <Button
              key={c.id}
              size="s"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                onChoice(event.tool === 'get_client_context' ? `Co wiemy o ${c.label}?` : `Chodzi o: ${c.label}`)
              }
            >
              {c.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Zadanie z rozmowy, w której AI widziało dane klienta (maile, notatki, spotkania): nic nie jest zapisane, dopóki osoba
 * nie potwierdzi — tytuł, opis i klient mogły pochodzić z polecenia ukrytego w treści maila.
 */
function ChatTaskConfirm({
  preview,
  onConfirmed,
}: {
  preview: ChatTaskPreview;
  /** wynik zapisany w rozmowie (karta po ponownym otwarciu panelu nie pokazuje znowu „Dodaj zadanie”) */
  onConfirmed?: (confirmed: ChatConfirmed) => void;
}) {
  const invalidate = useCopilotInvalidate();
  const create = trpc.tasks.create.useMutation();
  const resolveTurn = useResolveCopilotTurn();
  const [done, setDone] = useState<{ id: string; number: number; title: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setError(null);
    try {
      const task = await create.mutateAsync({
        title: preview.title,
        ...(preview.descriptionMd ? { descriptionMd: preview.descriptionMd } : {}),
        ...(preview.clientId ? { clientId: preview.clientId } : {}),
        ...(preview.projectId ? { projectId: preview.projectId } : {}),
        ...(preview.assigneeUserId ? { assigneeUserId: preview.assigneeUserId } : {}),
        ...(preview.dueDate ? { dueDate: preview.dueDate } : {}),
        ...(preview.priority ? { priority: preview.priority as never } : {}),
      });
      setDone({ id: task.id, number: task.number, title: task.title });
      resolveTurn();
      onConfirmed?.({
        label: `Dodano zadanie #${task.number} ${task.title}`,
        href: `/zadania/${task.id}`,
        assignee: task.assignee ? { userId: task.assignee.userId, name: task.assignee.name } : null,
      });
      void invalidate.tasks();
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się dodać zadania.'));
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-canvas p-3">
      <p className="text-body-s font-medium break-words text-fg">{preview.title}</p>
      <p className="text-caption text-fg-muted">
        {[preview.clientName, preview.dueDate ? `termin ${preview.dueDate}` : null].filter(Boolean).join(' · ') ||
          'Bez klienta'}
      </p>
      {preview.descriptionMd && (
        <p className="line-clamp-4 text-caption break-words whitespace-pre-line text-fg-secondary">
          {preview.descriptionMd}
        </p>
      )}
      <p className="text-caption text-fg-muted">{COPILOT_MESSAGES.taskNeedsConfirmation}</p>
      {done ? (
        <p className="text-caption text-fg">
          Dodano zadanie #{done.number}{' '}
          <Link to={`/zadania/${done.id}`} className="text-accent hover:underline">
            Otwórz
          </Link>
        </p>
      ) : (
        <div>
          <Button size="s" variant="secondary" loading={create.isPending} onClick={() => void confirm()}>
            Dodaj zadanie
          </Button>
        </div>
      )}
      {error && <FormMessage>{error}</FormMessage>}
    </div>
  );
}

/**
 * Zlecenie dla Claude z narzędzia `delegate_to_claude` (Fala 9, DECISIONS 2026-10-07 „Zmiany w kodzie i operacje robi
 * tylko Claude”): Copilot nie zmienia kodu ani repozytoriów — przygotował podgląd. Przebieg powstaje dopiero po
 * kliknięciu „Zleć Claude” (`agents.create` z UI); „Zmień…” otwiera pełne okno „Zleć Claude” z tymi danymi.
 * Instrukcja jest widoczna w całości (przewijane pole, bez ucinania). Długa instrukcja albo rozmowa z danymi klienta
 * (`delegateNeedsFullReview`) = bez zlecenia jednym kliknięciem — tylko „Sprawdź i zleć…” (pełne okno z polem
 * tekstowym i ostrzeżeniem); przegląd Fali 9.
 */
function ChatDelegateConfirm({
  preview,
  onConfirmed,
}: {
  preview: ChatDelegatePreview;
  onConfirmed?: (confirmed: ChatConfirmed) => void;
}) {
  const access = useAccess();
  const utils = trpc.useUtils();
  const create = trpc.agents.create.useMutation();
  const resolveTurn = useResolveCopilotTurn();
  const [done, setDone] = useState<{ id: string; number: number } | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const review = delegateNeedsFullReview(preview);
  const context = preview.task
    ? { taskId: preview.task.id }
    : preview.project
      ? { projectId: preview.project.id }
      : preview.client
        ? { clientId: preview.client.id }
        : {};
  const where = preview.task
    ? `#${preview.task.number} ${preview.task.title}`
    : (preview.project?.name ?? preview.client?.name ?? 'Bez klienta i projektu');
  const confirm = async () => {
    setError(null);
    try {
      const run = await create.mutateAsync({
        ...context,
        ...(preview.task ? {} : { title: preview.title }),
        instruction: preview.instruction,
        runtime: preview.runtime,
        ...(preview.runtime === 'external' ? { provider: 'claude_code' } : {}),
      });
      setDone({ id: run.id, number: run.number });
      resolveTurn();
      onConfirmed?.({ label: `Zlecono Claude — przebieg #${run.number}`, href: `/agenci/${run.id}` });
      void Promise.all([utils.agents.invalidate(), utils.tasks.invalidate()]);
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się zlecić pracy Claude.'));
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-canvas p-3">
      <p className="text-body-s font-medium break-words text-fg">{preview.title}</p>
      <p className="text-caption text-fg-muted">
        {where} · {DELEGATE_RUNTIME_LABELS[preview.runtime]}
      </p>
      {/* cała instrukcja (bez ucinania) — dokładnie ta treść trafi do Claude po potwierdzeniu; przewijane pole
          (Chrome daje fokus z klawiatury przewijanym blokom bez tabIndex) */}
      <section
        aria-label="Instrukcja dla Claude"
        className="max-h-48 overflow-y-auto rounded-md bg-surface px-2.5 py-2 focus-visible:outline-2 focus-visible:outline-accent"
      >
        <p className="text-caption break-words whitespace-pre-wrap text-fg-secondary">{preview.instruction}</p>
      </section>
      <p className="text-caption text-fg-muted">{delegationReasonText(preview)}</p>
      {preview.untrustedContext && <p className="text-caption text-warning">{COPILOT_MESSAGES.delegateUntrusted}</p>}
      <p className="text-caption text-fg-muted">
        {review ? COPILOT_MESSAGES.delegateReviewInDialog : COPILOT_MESSAGES.delegateNeedsConfirmation}
      </p>
      {done ? (
        <p className="text-caption text-fg">
          Zlecono Claude — przebieg #{done.number}{' '}
          <Link to={`/agenci/${done.id}`} className="text-accent hover:underline">
            Otwórz
          </Link>
        </p>
      ) : access.can('agents', 'edit') ? (
        review ? (
          <div>
            <Button size="s" variant="secondary" onClick={() => setEditing(true)}>
              Sprawdź i zleć…
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button size="s" variant="secondary" loading={create.isPending} onClick={() => void confirm()}>
              Zleć Claude
            </Button>
            <Button size="s" variant="ghost" disabled={create.isPending} onClick={() => setEditing(true)}>
              Zmień…
            </Button>
          </div>
        )
      ) : (
        <p className="text-caption text-fg-muted">Zlecić pracę Claude może osoba z modułem Agenci (Edycja).</p>
      )}
      {error && <FormMessage>{error}</FormMessage>}
      {editing && (
        <DelegateToClaudeDialog
          open
          onOpenChange={(o) => {
            if (!o) setEditing(false);
          }}
          target={{
            ...context,
            contextLabel: where,
            projectName: preview.project?.name ?? null,
            ...(preview.task ? {} : { title: preview.title }),
            instruction: preview.instruction,
            runtime: preview.runtime,
            ...(preview.untrustedContext ? { warning: COPILOT_MESSAGES.delegateUntrusted } : {}),
          }}
          onCreated={(run) => {
            setDone({ id: run.id, number: run.number });
            resolveTurn();
            onConfirmed?.({ label: `Zlecono Claude — przebieg #${run.number}`, href: `/agenci/${run.id}` });
          }}
        />
      )}
    </div>
  );
}

/** Podgląd wpisu czasu z narzędzia `log_time`: wybór klienta = ponowny podgląd (`time.parse`), zapis po potwierdzeniu. */
function ChatTimePreview({
  preview: initial,
  onSaved,
}: {
  preview: TimeEntryParsePreview;
  onSaved?: (label: string) => void;
}) {
  const utils = trpc.useUtils();
  const resolveTurn = useResolveCopilotTurn();
  const [preview, setPreview] = useState(initial);
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choose = async (clientId: string) => {
    setChoosing(true);
    setError(null);
    try {
      setPreview(await utils.time.parse.fetch({ text: initial.text, clientId }));
    } catch {
      setError('Nie udało się odświeżyć podglądu. Wpisz czas w module Czas.');
    } finally {
      setChoosing(false);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <TimePreviewCard
        preview={preview}
        choosing={choosing}
        onChooseClient={(id) => void choose(id)}
        onSaved={(label) => {
          resolveTurn();
          onSaved?.(label);
        }}
      />
      {error && <FormMessage>{error}</FormMessage>}
    </div>
  );
}
